<template>
    <div>
        <ValidationObserver ref="formValidate">
            <b-row>
                <b-col cols="12" md="6">
                    <ValidationProvider name="semester" rules="required" v-slot="{ valid, errors }">
                        <b-form-group :label="$t('semester')">
                            <semesters-selectbox v-model="form.semester_id"
                                                 disabled
                                                 :validate-error="errors[0]"></semesters-selectbox>
                        </b-form-group>
                    </ValidationProvider>
                </b-col>
                <b-col cols="12" md="6">
                    <b-form-group :label="$t('teaching_staff')">
                        <ValidationProvider name="instructor_id" rules="required" v-slot="{ errors }">
                            <staff-auto-complete v-model="form.instructor_id"
                                                 :valueObject="instructorValueObject"
                                                 disabled
                                                 :validate-error="errors[0]" />
                        </ValidationProvider>
                    </b-form-group>
                </b-col>
                <b-col cols="12">
                    <b-form-group :label="$t('course')">
                        <div class="label-as-input font-weight-bold" v-if="course">
                            {{ course.code }} - {{ getLocaleText(course, 'name') }}
                        </div>
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                    <ValidationProvider name="from_date" rules="required" v-slot="{ errors }">
                        <b-form-group :label="$t('from_date')">
                            <b-form-input type="datetime-local" v-model="form.from_date"></b-form-input>
                            <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"></b-form-invalid-feedback>
                        </b-form-group>
                    </ValidationProvider>
                </b-col>
                <b-col cols="12" md="6">
                    <ValidationProvider name="to_date" rules="required" v-slot="{ errors }">
                        <b-form-group :label="$t('to_date')">
                            <b-form-input type="datetime-local" v-model="form.due_date"></b-form-input>
                            <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"></b-form-invalid-feedback>
                        </b-form-group>
                    </ValidationProvider>
                </b-col>
                <b-col cols="12">
                    <ValidationProvider name="explanation" rules="required" v-slot="{errors}">
                        <b-form-group :label="$t('content')">
                            <ck-editor v-model="form.explanation"/>
                            <div class="invalid-feedback d-block" v-if="errors[0]" v-html="errors[0]"></div>
                        </b-form-group>
                    </ValidationProvider>
                </b-col>
                <b-col cols="12">
                    <ValidationProvider name="file" :rules="form.file_name?'':'required'" v-slot="{valid, errors}">
                        <b-form-group :label="$t('file')">
                            <div class="d-flex custom-file-upload">
                                <b-form-file v-model="form.file"
                                             :state="errors[0] ? false : (valid ? true : null)"
                                             :placeholder="form.file_name"
                                             :drop-placeholder="$t('drop_file')"
                                             ref="fileInput"
                                             multiple
                                />
                                <b-button variant="outline-secondary"
                                          @click="$refs.fileInput.$el.childNodes[0].click();">{{ $t('browse') }}
                                </b-button>
                            </div>
                            <div class="invalid-feedback d-block" v-if="errors[0]" v-html="errors[0]"></div>
                        </b-form-group>
                    </ValidationProvider>
                </b-col>
                <b-col cols="12">
                    <ValidationProvider name="section_ids" rules="required" v-slot="{ errors }">
                        <b-form-group :label="$t('section')">
                            <multi-selectbox v-model="form.section_ids"
                                             :options="sectionOptions"
                                             :multiple="true"
                                             :validate-error="errors[0]" />
                        </b-form-group>
                    </ValidationProvider>
                </b-col>
            </b-row>
            <div class="d-flex">
                <b-button class="mr-2" squared variant="primary" v-b-tooltip.hover :title="$t('show')"
                          @click="showFile()">
                    <i class="ri-file-download-line" style="font-size: 25px"></i>
                </b-button>
                <b-button variant="primary" @click="save">{{ $t('save').toUpper() }}</b-button>
            </div>
        </ValidationObserver>
    </div>
</template>
<script>

import moment from "moment/moment";
import LmsHomeWorkEntryService from "@/services/LmsHomeWorkEntryService"
import SectionService from "@/services/SectionService"
import CkEditor from "@/components/interactive-fields/Ckeditor.vue"
import MultiSelectbox from "@/components/interactive-fields/MultiSelectbox.vue"

export default {
    components: {
        MultiSelectbox,
        CkEditor
    },
    props: {
        semesterId: {},
        formId: {
            type: Number
        },
        course: {
            type: Object
        },
    },
    data() {
        return {
            form: {
                explanation: null,
                file_name: null,
                from_date: null,
                due_date: null,
                instructor_id: null,
                fileData: null,
                section_ids: [],
            },
            instructorValueObject: {},
            sectionOptions: []
        }
    },
    created() {
        this.getSections()
        this.getData()
    },
    methods: {
        getSections() {
            this.sectionOptions = []
            const courseId = this.course ? this.course.id : this.form.course_id
            const semesterId = this.semesterId || this.form.semester_id
            if (!courseId || !semesterId) return

            const config = {
                params: {
                    course_id: courseId,
                    semester_id: semesterId
                },
            }
            SectionService.instructorSections(config).then(response => {
                if (response.data && response.data.data) {
                    response.data.data.forEach(item => {
                        this.sectionOptions.push({
                            value: item.section_id,
                            text: item.section
                        })
                    })
                    this.sectionOptions.sort((a, b) => a.text - b.text)
                }
            }).catch(e => {
                console.error(e)
            })
        },
        getData(){
            LmsHomeWorkEntryService.show(this.formId).then(response => {
                let data = response.data.data
                this.instructorValueObject = {
                    value: data.instructor_id,
                    text: data.instructor_name + ' - ' + data.instructor_surname
                }
                this.form = {
                    explanation: data.content ? this.decodeHTMLEntities(data.content) : null,
                    section_ids: data.section_ids || [],
                    instructor_id: data.instructor_id,
                    course_id: data.course_id,
                    semester_id: data.semester_id,
                    from_date: data.from_date,
                    due_date: data.due_date,
                    file: null,
                    file_name: data.file ? data.file.file_name : null,
                    fileData: data.file ? data.file : null,
                }
                if (!this.sectionOptions.length) {
                    this.getSections()
                }
            }).catch(err => {
                this.showErrors(err)
            })
        },
        async save() {
            const isValid = await this.$refs.formValidate.validate();
            if (isValid){
                const formData = new FormData()
                const dueDate = moment(this.form.due_date).format('YYYY-MM-DD HH:mm:ss')
                const fromDate = moment(this.form.from_date).format('YYYY-MM-DD HH:mm:ss')

                formData.append('explanation', this.form.explanation)
                formData.append('semester_id', this.semesterId || this.form.semester_id)
                formData.append('course_id', this.form.course_id)
                formData.append('instructor_id', this.form.instructor_id)
                formData.append('from_date', fromDate)
                formData.append('due_date', dueDate)

                if (this.form.file) {
                    formData.append('file', this.form.file[0])
                }

                if (Array.isArray(this.form.section_ids)) {
                    this.form.section_ids.forEach(id => {
                        formData.append('section_ids[]', id)
                    })
                } else if (this.form.section_ids) {
                    formData.append('section_ids[]', this.form.section_ids)
                }

                LmsHomeWorkEntryService.update(this.formId, formData).then(response => {
                    this.$toast.success(this.$t('api.' + response.data.message))
                    this.$emit('homeworkSuccess', true)
                }).catch(err => {
                    this.showErrors(err, this.$refs.formValidate)
                })
            }
        },
        showFile(){
            if(this.form.fileData && this.form.fileData.uuid){
                LmsHomeWorkEntryService.downloadFile(this.form.fileData.uuid)
                    .then(response => {
                        this._downloadFile(response, this.form.fileData.file_name)
                    }).catch(err => {
                    this.showErrors(err)
                })
            } else {
                this.$toast.error(this.$t('file_not_found'))
            }
        }
    },

};
</script>
